import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShoppingBag, X, Trash2, Gift, Truck } from "lucide-react";
import { useCart } from "../context/CartContext";

const eur = (n) => `€${n.toLocaleString("en-DE")}`;

export function MiniCart() {
  const { items, count, subtotal, discount, promoCode, freeShippingRemaining, removeItem } = useCart();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-cart"
        aria-label={`Cart, ${count} item${count === 1 ? "" : "s"}`}
        aria-expanded={open}
        className="relative inline-flex items-center gap-2 hover:text-[#C8A96A] transition-colors"
      >
        <ShoppingBag size={18} />
        <span className="text-[11px] tracking-[0.16em] uppercase">Cart ({count})</span>
        {count > 0 && (
          <span
            data-testid="nav-cart-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#C8A96A] text-[#0C182E] text-[9px] font-semibold flex items-center justify-center"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mini-cart"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 mt-4 w-[360px] max-w-[90vw] bg-[#F4EDE2] text-[#0C111C] border border-[#0C111C]/10 shadow-[0_30px_80px_rgba(12,17,28,0.22)] z-50"
            role="dialog"
            aria-label="Mini cart"
          >
            {/* header */}
            <div
              className="flex items-center justify-between px-5 py-4 border-b border-[#0C111C]/10"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <span className="text-[11px] tracking-[0.22em] uppercase">
                Your bag · {count}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="mini-cart-close"
                className="text-[#0C111C]/60 hover:text-[#0C111C]"
              >
                <X size={16} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="px-5 py-10 text-center" data-testid="mini-cart-empty">
                <p
                  className="text-lg"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  Nothing in your bag yet.
                </p>
                <Link
                  to="/collection"
                  onClick={() => setOpen(false)}
                  data-testid="mini-cart-empty-cta"
                  className="mt-5 inline-flex items-center gap-2 bg-[#0C182E] hover:bg-[#0C111C] text-[#F4EDE2] px-5 h-11 text-[11px] tracking-[0.22em] uppercase transition-colors"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  Explore the collection →
                </Link>
              </div>
            ) : (
              <>
                {/* free-shipping strip */}
                {freeShippingRemaining > 0 ? (
                  <div
                    className="px-5 py-3 bg-[#EDE2CE] text-[11px] tracking-[0.18em] uppercase flex items-center gap-2 border-b border-[#0C111C]/10"
                    style={{ fontFamily: "Inter, sans-serif" }}
                    data-testid="mini-cart-shipping"
                  >
                    <Truck size={13} className="text-[#C8A96A]" />
                    {eur(freeShippingRemaining)} to free shipping
                  </div>
                ) : (
                  <div
                    className="px-5 py-3 bg-[#EDE2CE] text-[11px] tracking-[0.18em] uppercase flex items-center gap-2 border-b border-[#0C111C]/10 text-[#C8A96A]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                    data-testid="mini-cart-shipping"
                  >
                    <Truck size={13} /> Free shipping unlocked
                  </div>
                )}

                {/* items */}
                <div className="max-h-[45vh] overflow-y-auto divide-y divide-[#0C111C]/10">
                  {items.map((i) => (
                    <div
                      key={i.id}
                      className="px-5 py-4 flex gap-3"
                      data-testid={`mini-cart-line-${i.slug}`}
                    >
                      <Link
                        to={`/product/${i.slug}`}
                        onClick={() => setOpen(false)}
                        className="w-14 h-20 shrink-0 bg-[#EDE2CE] overflow-hidden"
                      >
                        <img
                          src={i.product.image}
                          alt={i.product.name}
                          className="h-full w-full object-cover"
                        />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <Link
                            to={`/product/${i.slug}`}
                            onClick={() => setOpen(false)}
                            className="text-sm leading-tight hover:text-[#C8A96A]"
                            style={{ fontFamily: "Fraunces, serif" }}
                          >
                            {i.product.name}
                          </Link>
                          <button
                            type="button"
                            onClick={() => removeItem(i.id)}
                            aria-label={`Remove ${i.product.name}`}
                            data-testid={`mini-cart-remove-${i.slug}`}
                            className="text-[#0C111C]/40 hover:text-[#B95841]"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                        <p
                          className="mt-0.5 text-[11px] tracking-[0.1em] uppercase text-[#3A3A3E]"
                          style={{ fontFamily: "Inter, sans-serif" }}
                        >
                          {i.size} · Qty {i.qty}
                        </p>
                        {i.gift && (
                          <p
                            className="mt-1 flex items-start gap-1 text-[11px] italic text-[#C8A96A]"
                            style={{ fontFamily: "Fraunces, serif" }}
                          >
                            <Gift size={10} className="mt-[3px] shrink-0" />
                            "{i.gift.length > 40 ? i.gift.slice(0, 40) + "…" : i.gift}"
                          </p>
                        )}
                        <p
                          className="mt-1 text-sm"
                          style={{ fontFamily: "Fraunces, serif" }}
                        >
                          {eur(i.lineTotal)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* summary + CTAs */}
                <div className="px-5 py-4 border-t border-[#0C111C]/10 bg-[#EDE2CE]/40">
                  {discount > 0 && (
                    <div
                      className="flex items-center justify-between text-[11px] tracking-[0.16em] uppercase text-[#C8A96A] mb-1"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      <span>{promoCode} · -10%</span>
                      <span>-{eur(discount)}</span>
                    </div>
                  )}
                  <div
                    className="flex items-baseline justify-between"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    <span className="text-[11px] tracking-[0.22em] uppercase text-[#0C111C]/70">
                      Subtotal
                    </span>
                    <span
                      className="text-xl"
                      style={{ fontFamily: "Fraunces, serif" }}
                      data-testid="mini-cart-subtotal"
                    >
                      {eur(subtotal - discount)}
                    </span>
                  </div>
                  <p
                    className="mt-1 text-[10px] tracking-[0.18em] uppercase text-[#0C111C]/50"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Taxes and shipping calculated at checkout
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <Link
                      to="/cart"
                      onClick={() => setOpen(false)}
                      data-testid="mini-cart-view"
                      className="inline-flex items-center justify-center border border-[#0C182E] text-[#0C182E] hover:bg-[#0C182E] hover:text-[#F4EDE2] h-11 text-[11px] tracking-[0.22em] uppercase transition-colors"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      View bag
                    </Link>
                    <Link
                      to="/cart"
                      onClick={() => setOpen(false)}
                      data-testid="mini-cart-checkout"
                      className="inline-flex items-center justify-center bg-[#0C182E] text-[#F4EDE2] hover:bg-[#0C111C] h-11 text-[11px] tracking-[0.22em] uppercase transition-colors"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      Checkout
                    </Link>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
